import React, { useEffect, useState } from "react";
import { Accordion, Table, Form, Button, Tabs, Tab } from "react-bootstrap";
import axios from "axios";
import { BASE_URL } from "../../config";
import { GeoJsonLayer } from "@deck.gl/layers";
import { getAccessToken } from "../../utils/LoginForm/Auth/auth";
import { showMapConfirmToast } from "../../utils/Toast/mapToast";
import "./ReadingWalkPanel.css";

const ReadingWalkPanel = ({ setOverlay, setWfsLayers }) => {
    const [searchParams, setSearchParams] = useState({ reader: "", date: "" });
    const [summaryData, setSummaryData] = useState(null);
    const [periods, setPeriods] = useState([]);
    const [ohDetailsMap, setOhDetailsMap] = useState({});
    const [meterDetails, setMeterDetails] = useState({});
    const [openOHIds, setOpenOHIds] = useState({});
    const [openMeterIds, setOpenMeterIds] = useState({});
    const [statistics, setStatistics] = useState(null);
    const [selectedPeriodForStats, setSelectedPeriodForStats] = useState("");
    const [selectedOhId, setSelectedOhId] = useState(null);
    const [highlightedIdsReader, setHighlightedIdsReader] = useState([]);
    const [highlightedIdsStats, setHighlightedIdsStats] = useState([]);
    const [citaci, setCitaci] = useState([]);
    const [activeTab, setActiveTab] = useState("reader");
    const [ohSearch, setOhSearch] = useState("");
    const [ohResults, setOhResults] = useState([]);

    const token = getAccessToken();

    const parsePeriodStart = (period) => {
        if (!period || typeof period !== "string") return new Date(0);

        const start = period.split("-")[0];
        const [day, month, year] = start.split(".").map(Number);

        if (!day || !month || !year) return new Date(0);

        return new Date(year, month - 1, day);
    };

    const sortPeriodsDesc = (items = []) => {
        return [...items].sort((a, b) => parsePeriodStart(b) - parsePeriodStart(a));
    };

    const showToast = ({ title, message, variant = "info" }) => {
        showMapConfirmToast({
            title,
            message,
            confirmText: "U redu",
            cancelText: "",
            variant,
            autoClose: 3000,
            showTimer: true,
        });
    };

    useEffect(() => {
        let isMounted = true;

        axios
            .get(`${BASE_URL}/api/reading-walks/periods`)
            .then((res) => {
                if (isMounted) setPeriods(sortPeriodsDesc(res.data || []));
            })
            .catch((err) => {
                console.error("Greška pri dohvatu perioda:", err);
                showToast({
                    title: "Greška",
                    message: "Greška pri dohvatu obračunskih perioda.",
                    variant: "danger",
                });
            });

        axios
            .get(`${BASE_URL}/geoserver/ows`, {
                params: {
                    service: "WFS",
                    version: "1.0.0",
                    request: "GetFeature",
                    typeName: "elektro:cl_citaci",
                    outputFormat: "application/json",
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((res) => {
                if (isMounted) {
                    const features = res.data.features || [];

                    const lista = features
                        .map((f) => {
                            const p = f.properties || {};

                            return {
                                id: p.id_citaci || p.id || p.gid || p.ID,
                                naziv:
                                    p.ime_i_prezime ||
                                    p.naziv ||
                                    p.ime_prezime ||
                                    p.ime_citaca ||
                                    p.korisnik ||
                                    p.name ||
                                    p.ime ||
                                    "Nepoznato",
                            };
                        })
                        .filter((c) => c.id && c.naziv);

                    setCitaci(lista);
                }
            })
            .catch((err) => {
                console.error("Greška pri dohvatu čitača:", err);
                if (isMounted) setCitaci([]);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const handleInputChange = (e) => {
        setSearchParams({
            ...searchParams,
            [e.target.name]: e.target.value,
        });
    };

    const handleSearch = async () => {
        if (!searchParams.reader || !searchParams.date) {
            showToast({
                title: "Greška",
                message: "Molimo popunite oba polja: Očitač i Obračunski period.",
                variant: "danger",
            });
            return;
        }

        setOverlay({ active: true, message: "Pretraga očitačkog hoda..." });
        updateMultipleOHLayer([], "reader");
        updateSelectedOHLayer(null);

        try {
            const res = await axios.get(`${BASE_URL}/api/reading-walks`, {
                params: {
                    reader: searchParams.reader,
                    date: searchParams.date,
                },
            });

            setSummaryData(res.data);

            if (!res.data || !res.data.data || res.data.data.length === 0) {
                showToast({
                    title: "Nema rezultata",
                    message: "Nema pronađenih očitačkih hodova za izabranog očitača i period.",
                    variant: "warning",
                });
            }

            setOhDetailsMap({});
            setMeterDetails({});
            setOpenOHIds({});
            setOpenMeterIds({});
            setSelectedOhId(null);
            updateSelectedOHLayer(null);
        } catch (error) {
            console.error("Greška:", error);
            showToast({
                title: "Greška",
                message: "Greška prilikom učitavanja očitačkih hodova.",
                variant: "danger",
            });
        } finally {
            setTimeout(() => setOverlay({ active: false, message: "" }), 500);
        }
    };

    const fetchOHDetails = async (ohId) => {
        if (ohDetailsMap[ohId]) return;

        setOverlay({ active: true, message: `Učitavanje podataka za OH #${ohId}` });

        try {
            const response = await axios.get(`${BASE_URL}/api/reading-walks/${ohId}`);
            setOhDetailsMap((prev) => ({
                ...prev,
                [ohId]: { ...response.data, id_oh: ohId },
            }));
        } catch (error) {
            console.error("Greška prilikom preuzimanja detalja za OH:", error);
            showToast({
                title: "Greška",
                message: "Greška pri učitavanju detalja očitačkog hoda.",
                variant: "danger",
            });
        } finally {
            setTimeout(() => setOverlay({ active: false, message: "" }), 500);
        }
    };

    const fetchMeterDetails = async (id_mm) => {
        if (meterDetails[id_mm]) return;

        setOverlay({ active: true, message: `Učitavanje podataka za brojilo #${id_mm}` });

        try {
            const res = await axios.get(`${BASE_URL}/api/reading-walks/meter/${id_mm}`);
            setMeterDetails((prev) => ({ ...prev, [id_mm]: res.data }));
        } catch (err) {
            console.error("Greška pri dohvaćanju detalja brojila:", err);
            showToast({
                title: "Greška",
                message: "Greška pri učitavanju detalja brojila.",
                variant: "danger",
            });
        } finally {
            setTimeout(() => setOverlay({ active: false, message: "" }), 500);
        }
    };

    const toggleOHAccordion = (ohId) => {
        setOpenOHIds((prev) => {
            const isOpen = prev[ohId];

            if (!isOpen) {
                if (!ohDetailsMap[ohId]) fetchOHDetails(ohId);
                updateSelectedOHLayer(ohId);
            } else {
                updateSelectedOHLayer(null);
            }

            return { ...prev, [ohId]: !isOpen };
        });
    };

    const toggleMeterAccordion = (id_mm) => {
        setOpenMeterIds((prev) => {
            const isOpen = prev[id_mm];

            if (!isOpen && !meterDetails[id_mm]) {
                fetchMeterDetails(id_mm);
            }

            return { ...prev, [id_mm]: !isOpen };
        });
    };

    const readingMethodLabels = {
        1: "Očitanje",
        3: "Indirektna potrošnja",
        4: "Penal",
        6: "Odobrenje/Zaduženje",
        9: "Obračun",
    };

    const getReadingMethodLabel = (value) => readingMethodLabels[value] || value;

    const handleStatsSearch = async () => {
        if (!selectedPeriodForStats) {
            showToast({
                title: "Greška",
                message: "Molimo odaberite obračunski period.",
                variant: "danger",
            });
            return;
        }

        setOverlay({ active: true, message: "Učitavanje statistike za period..." });

        try {
            const res = await axios.get(`${BASE_URL}/api/reading-walks/period-statistics`, {
                params: { date: selectedPeriodForStats },
            });

            setStatistics(res.data);

            if (!res.data || !res.data.readingWalksByMeterCount?.length) {
                showToast({
                    title: "Nema rezultata",
                    message: "Nema dostupne statistike za izabrani obračunski period.",
                    variant: "warning",
                });
            }
        } catch (err) {
            console.error("Greška pri dohvaćanju statistike:", err);
            showToast({
                title: "Greška",
                message: "Greška pri dohvaćanju statistike.",
                variant: "danger",
            });
        } finally {
            setTimeout(() => setOverlay({ active: false, message: "" }), 500);
        }
    };

    const displayValue = (value) =>
        value !== undefined && value !== null && value !== "" ? value : "Nepoznato";

    const updateSelectedOHLayer = (ohId) => {
        setSelectedOhId(ohId);
        updateOHLayerStyle(ohId, highlightedIdsReader, highlightedIdsStats);
    };

    const updateMultipleOHLayer = (activeIds, source = "reader") => {
        if (source === "reader") {
            setHighlightedIdsReader(activeIds);
            updateOHLayerStyle(selectedOhId, activeIds, highlightedIdsStats);
        } else {
            setHighlightedIdsStats(activeIds);
            updateOHLayerStyle(selectedOhId, highlightedIdsReader, activeIds);
        }
    };

    const handleRowClick = (idOhList, source = "reader") => {
        if (!idOhList) return;

        if (
            typeof idOhList === "string" &&
            idOhList.startsWith("{") &&
            idOhList.endsWith("}")
        ) {
            idOhList = idOhList
                .slice(1, -1)
                .split(",")
                .map((id) => parseInt(id.replace(/"/g, ""), 10))
                .filter((id) => !isNaN(id));
        }

        if (!Array.isArray(idOhList) || idOhList.length === 0) return;

        const current = source === "reader" ? highlightedIdsReader : highlightedIdsStats;
        const alreadyIncluded = idOhList.every((id) => current.includes(id));

        const updated = alreadyIncluded
            ? current.filter((id) => !idOhList.includes(id))
            : [...new Set([...current, ...idOhList])];

        updateMultipleOHLayer(updated, source);
    };

    const updateOHLayerStyle = (selectedId, readerIds, statsIds) => {
        const defaultColor = [0, 123, 255, 150];
        const selectedColor = [255, 0, 0, 255];
        const readerColor = [58, 51, 96, 255];
        const statsColor = [0, 200, 83, 255];

        setWfsLayers((prevLayers) =>
            prevLayers.map((layer) => {
                if (layer.id === "ocitacki_hod-layer") {
                    const data = layer.props.data;

                    const newGetLineColor = (feature) => {
                        const id = feature.properties.id_oh;
                        if (id === selectedId) return selectedColor;
                        if (readerIds.includes(id)) return readerColor;
                        if (statsIds.includes(id)) return statsColor;
                        return defaultColor;
                    };

                    const newGetLineWidth = (feature) => {
                        const id = feature.properties.id_oh;
                        if (id === selectedId) return 10;
                        if (readerIds.includes(id) || statsIds.includes(id)) return 6;
                        return 2;
                    };

                    return new GeoJsonLayer({
                        ...layer.props,
                        data,
                        getLineColor: newGetLineColor,
                        getLineWidth: newGetLineWidth,
                        updateTriggers: {
                            getLineColor: `${selectedId}-${readerIds.join(",")}-${statsIds.join(",")}`,
                            getLineWidth: `${selectedId}-${readerIds.join(",")}-${statsIds.join(",")}`,
                        },
                    });
                }

                return layer;
            })
        );
    };

    const handleReaderReset = () => {
        setSearchParams({ reader: "", date: "" });
        setSummaryData(null);
        setOhDetailsMap({});
        setMeterDetails({});
        setOpenOHIds({});
        setOpenMeterIds({});
        setSelectedOhId(null);
        setHighlightedIdsReader([]);
        updateOHLayerStyle(null, [], highlightedIdsStats);
    };

    const handleStatsReset = () => {
        setSelectedPeriodForStats("");
        setStatistics(null);
        setHighlightedIdsStats([]);
        updateOHLayerStyle(selectedOhId, highlightedIdsReader, []);
    };

    const handleOhReset = () => {
        setOhSearch("");
        setOhResults([]);
    };

    const handleOhSearch = async () => {
        if (!ohSearch.trim()) {
            showToast({
                title: "Greška",
                message: "Molimo unesite naziv očitačkog hoda.",
                variant: "danger",
            });
            return;
        }

        setOverlay({ active: true, message: "Pretraga po nazivu očitačkog hoda..." });

        try {
            const res = await axios.get(`${BASE_URL}/api/reading-walks/by-name`, {
                params: { oh: ohSearch },
            });

            const results = res.data || [];

            if (results.length === 0) {
                showToast({
                    title: "Nema rezultata",
                    message: "Nema pronađenih očitačkih hodova za zadati naziv.",
                    variant: "warning",
                });
            }

            setOhResults(results);
        } catch (err) {
            console.error("Greška pri pretrazi po OH nazivu:", err);
            setOhResults([]);

            showToast({
                title: "Greška",
                message: "Greška pri pretrazi po nazivu očitačkog hoda.",
                variant: "danger",
            });
        } finally {
            setTimeout(() => setOverlay({ active: false, message: "" }), 500);
        }
    };

    return (
        <div className="readingwalk-panel">
            <h5 style={{ color: "#ffffff", marginBottom: "5px" }}>Pretraga po:</h5>

            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                <Tab eventKey="reader" title="Očitaču">
                    <Form className="mb-1">
                        <Form.Group controlId="formReader">
                            <Form.Label>Očitač</Form.Label>

                            {citaci.length > 0 ? (
                                <Form.Select
                                    name="reader"
                                    value={searchParams.reader}
                                    onChange={handleInputChange}
                                >
                                    <option value="">-- Odaberi očitača --</option>
                                    {citaci.map((c) => (
                                        <option key={c.id} value={c.naziv}>
                                            {c.naziv}
                                        </option>
                                    ))}
                                </Form.Select>
                            ) : (
                                <Form.Control
                                    type="text"
                                    placeholder="Unesite ime očitača"
                                    name="reader"
                                    value={searchParams.reader}
                                    onChange={handleInputChange}
                                />
                            )}
                        </Form.Group>

                        <Form.Group controlId="formDate" className="mt-2">
                            <Form.Label>Obračunski period</Form.Label>
                            <Form.Select
                                name="date"
                                value={searchParams.date}
                                onChange={handleInputChange}
                            >
                                <option value="">-- Odaberi obračunski period --</option>
                                {periods.map((period) => (
                                    <option key={period} value={period}>
                                        {period}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Button onClick={handleSearch} className="toggle-details-button">
                            Pretraži
                        </Button>

                        {(summaryData?.count > 0 || searchParams.reader || searchParams.date) && (
                            <Button
                                onClick={handleReaderReset}
                                variant="danger"
                                style={{ width: "100%", marginTop: "10px" }}
                            >
                                Poništi pretragu
                            </Button>
                        )}
                    </Form>

                    {summaryData && summaryData.count > 0 && (
                        <Accordion alwaysOpen className="mt-3">
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>Pronađeni očitački hodovi</Accordion.Header>
                                <Accordion.Body>
                                    <p>
                                        <strong>Ukupan broj pronađenih OH:</strong> {summaryData.count}
                                    </p>

                                    {summaryData.data.map((oh) => {
                                        const ohData = ohDetailsMap[oh.id_oh];
                                        const isOpen = openOHIds[oh.id_oh];

                                        return (
                                            <Accordion
                                                key={oh.id_oh}
                                                className="mt-3"
                                                activeKey={isOpen ? `oh-${oh.id_oh}` : null}
                                            >
                                                <Accordion.Item eventKey={`oh-${oh.id_oh}`}>
                                                    <Accordion.Header onClick={() => toggleOHAccordion(oh.id_oh)}>
                                                        OH #{oh.id_oh} — {oh.ime_citaca}
                                                    </Accordion.Header>

                                                    <Accordion.Body>
                                                        {ohData ? (
                                                            <>
                                                                <p>
                                                                    <strong>Očitač:</strong> {ohData.reader}
                                                                </p>
                                                                <p>
                                                                    <strong>Ukupan broj brojila:</strong>{" "}
                                                                    {ohData.totalMeters}
                                                                </p>
                                                                <p>
                                                                    <strong>
                                                                        Datum zadnjeg obračunskog očitanja:
                                                                    </strong>{" "}
                                                                    {ohData.lastReadingDate}
                                                                </p>
                                                                <p>
                                                                    <strong>Dužina očitačkog hoda:</strong>{" "}
                                                                    {ohData.lengthMeters} m
                                                                </p>

                                                                <h6>Broj brojila po načinu očitanja:</h6>

                                                                <Table className="feature-info-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Način očitanja</th>
                                                                            <th>Broj brojila</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {Object.entries(ohData.metersByType).map(
                                                                            ([type, count]) => (
                                                                                <tr key={type}>
                                                                                    <td>{getReadingMethodLabel(type)}</td>
                                                                                    <td>{displayValue(count)}</td>
                                                                                </tr>
                                                                            )
                                                                        )}
                                                                    </tbody>
                                                                </Table>

                                                                <h6>Lista brojila</h6>

                                                                <Accordion alwaysOpen>
                                                                    {ohData.idMmList?.map((id_mm) => {
                                                                        const meter = meterDetails[id_mm];

                                                                        return (
                                                                            <Accordion.Item
                                                                                key={id_mm}
                                                                                eventKey={`meter-${id_mm}`}
                                                                            >
                                                                                <Accordion.Header
                                                                                    onClick={() => toggleMeterAccordion(id_mm)}
                                                                                >
                                                                                    ID MM: {id_mm}
                                                                                </Accordion.Header>

                                                                                <Accordion.Body>
                                                                                    {meter ? (
                                                                                        <Table className="feature-info-table">
                                                                                            <tbody>
                                                                                                <tr>
                                                                                                    <td>
                                                                                                        <strong>EIC</strong>
                                                                                                    </td>
                                                                                                    <td>{displayValue(meter.eic)}</td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <td>
                                                                                                        <strong>AMT</strong>
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        {displayValue(
                                                                                                            meter.potrosnja_mjesecna_amt
                                                                                                        )}
                                                                                                    </td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <td>
                                                                                                        <strong>AVT</strong>
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        {displayValue(
                                                                                                            meter.potrosnja_mjesecna_avt
                                                                                                        )}
                                                                                                    </td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <td>
                                                                                                        <strong>Obračunska snaga</strong>
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        {displayValue(
                                                                                                            meter.obracunska_snaga
                                                                                                        )}
                                                                                                    </td>
                                                                                                </tr>
                                                                                                <tr>
                                                                                                    <td>
                                                                                                        <strong>RVT</strong>
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        {displayValue(
                                                                                                            meter.obracunata_prekovremena_rvt
                                                                                                        )}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            </tbody>
                                                                                        </Table>
                                                                                    ) : (
                                                                                        <p>Učitavanje podataka za brojilo...</p>
                                                                                    )}
                                                                                </Accordion.Body>
                                                                            </Accordion.Item>
                                                                        );
                                                                    })}
                                                                </Accordion>
                                                            </>
                                                        ) : (
                                                            <p>Klikni na zaglavlje da vidiš detalje.</p>
                                                        )}
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        );
                                    })}
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    )}
                </Tab>

                <Tab eventKey="stats" title="Obrač. periodu">
                    <Form className="mb-1">
                        <Form.Group controlId="formStatDate">
                            <Form.Label>Obračunski period</Form.Label>
                            <Form.Select
                                value={selectedPeriodForStats}
                                onChange={(e) => setSelectedPeriodForStats(e.target.value)}
                            >
                                <option value="">-- Odaberi obračunski period --</option>
                                {periods.map((period) => (
                                    <option key={period} value={period}>
                                        {period}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Button onClick={handleStatsSearch} className="toggle-details-button">
                            Prikaži statistiku
                        </Button>

                        {(statistics?.count > 0 || selectedPeriodForStats) && (
                            <Button
                                onClick={handleStatsReset}
                                variant="danger"
                                style={{ width: "100%", marginTop: "10px" }}
                            >
                                Poništi pretragu
                            </Button>
                        )}
                    </Form>

                    {statistics && statistics.count > 0 && (
                        <>
                            <h6 className="mt-3">
                                Ukupan broj očitačkih hodova: {statistics.totalReadingWalks}
                            </h6>

                            <h6 className="mt-3">Pregled OH po broju brojila</h6>
                            <Table className="feature-info-table">
                                <thead>
                                    <tr>
                                        <th>Raspon</th>
                                        <th>Broj OH</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {statistics.readingWalksByMeterCount.map((row) => (
                                        <tr key={row.range}>
                                            <td>{row.range}</td>
                                            <td>{row.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <h6>Broj angažovanih čitača: {statistics.uniqueReaders}</h6>

                            <h6 className="mt-3">Broj brojila po načinu unosa očitanja</h6>
                            <Table className="feature-info-table">
                                <thead>
                                    <tr>
                                        <th>Način unosa</th>
                                        <th>Broj brojila</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {statistics.metersByReadingMethod.map((row, idx) => (
                                        <tr key={idx}>
                                            <td>{displayValue(row.nacin_unosa_ocitanja)}</td>
                                            <td>{displayValue(row.count)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <h6 className="mt-3">Broj brojila po obračunskom periodu</h6>
                            <Table className="feature-info-table">
                                <thead>
                                    <tr>
                                        <th>Obračunski period</th>
                                        <th>Broj brojila</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {statistics.metersByPeriod.map((row, idx) => (
                                        <tr key={idx}>
                                            <td>{displayValue(row.obracunski_period)}</td>
                                            <td>{displayValue(row.count)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <h6 className="mt-3">Pregled OH po dužini</h6>
                            <Table className="feature-info-table">
                                <thead>
                                    <tr>
                                        <th>Raspon dužine</th>
                                        <th>Broj OH</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {statistics.readingWalksByLength.map((row) => (
                                        <tr
                                            key={row.range}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleRowClick(row.ids, "stats")}
                                        >
                                            <td>{row.range}</td>
                                            <td>{row.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Tab>

                <Tab eventKey="oh" title="Nazivu OH">
                    <Form className="mb-1">
                        <Form.Group>
                            <Form.Label>Naziv očitačkog hoda</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Unesite naziv OH"
                                value={ohSearch}
                                onChange={(e) => setOhSearch(e.target.value)}
                            />
                        </Form.Group>

                        <Button onClick={handleOhSearch} className="toggle-details-button">
                            Pretraži
                        </Button>

                        {(ohResults.length > 0 || ohSearch.trim() !== "") && (
                            <Button
                                onClick={handleOhReset}
                                variant="danger"
                                style={{ width: "100%", marginTop: "10px" }}
                            >
                                Poništi pretragu
                            </Button>
                        )}
                    </Form>

                    {ohResults.length > 0 && (
                        <>
                            {ohResults.map((item, index) => (
                                <Table key={index} className="readingwalk-table">
                                    <tbody>
                                        <tr>
                                            <td className="key-column">ID MM</td>
                                            <td>{item.id_mm}</td>
                                        </tr>
                                        <tr>
                                            <td className="key-column">OH</td>
                                            <td>{item.oh}</td>
                                        </tr>
                                        <tr>
                                            <td className="key-column">Ime čitača</td>
                                            <td>{item.ime_citaca}</td>
                                        </tr>
                                        <tr>
                                            <td className="key-column">Datum upisa</td>
                                            <td>{item.datum_upisa}</td>
                                        </tr>
                                        <tr>
                                            <td className="key-column">Datum prestanka</td>
                                            <td>{item.datum_prestanka}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            ))}
                        </>
                    )}
                </Tab>
            </Tabs>
        </div>
    );
};

export default ReadingWalkPanel;